import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useDraft, useDraftActions } from '@/hooks/useCatalogEditDraft.ts';
import type { CatalogKind } from '@/types/catalog.ts';
import CatalogDiffViewer from '@/components/catalog/CatalogDiffViewer.tsx';

export default function SuperUserCatalogEditPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { draftId } = useParams<{ draftId: string }>();
  const { draft, loading, error, refetch, setDraft } = useDraft(draftId);
  const actions = useDraftActions();

  useEffect(() => {
    if (draft?.status === 'Confirmed') {
      const timer = setTimeout(() => navigate('/superuser/catalog'), 800);
      return () => clearTimeout(timer);
    }
    if (draft?.status === 'Discarded') {
      const timer = setTimeout(() => navigate('/superuser/catalog'), 800);
      return () => clearTimeout(timer);
    }
  }, [draft?.status, navigate]);

  async function handleConfirm() {
    if (!draft) return;
    try {
      const updated = await actions.confirm(draft.id, draft.kind as CatalogKind);
      setDraft(updated);
      toast.success(t('superuser.catalog.toast.confirmed', { defaultValue: 'Cambios aplicados.' }));
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'ERROR';
      toast.error(t(`superuser.catalog.errors.${raw}`, { defaultValue: raw }));
    }
  }

  async function handleDiscard() {
    if (!draft) return;
    if (!window.confirm(t('superuser.catalog.confirmDiscard', {
      defaultValue: 'Descartar borrador?',
    }))) return;
    try {
      const updated = await actions.discard(draft.id);
      setDraft(updated);
      toast.success(t('superuser.catalog.toast.discarded', { defaultValue: 'Borrador descartado.' }));
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'ERROR';
      toast.error(t(`superuser.catalog.errors.${raw}`, { defaultValue: raw }));
    }
  }

  async function handleRetry() {
    if (!draft) return;
    try {
      const updated = await actions.retry(draft.id);
      setDraft(updated);
      toast.info(t('superuser.catalog.toast.retrying', { defaultValue: 'Re-traduciendo…' }));
      // re-fetch to start polling again
      refetch();
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'ERROR';
      toast.error(t(`superuser.catalog.errors.${raw}`, { defaultValue: raw }));
    }
  }

  if (loading && !draft) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        {t('common.loading', { defaultValue: 'Cargando…' })}
      </div>
    );
  }

  if (error || !draft) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{error ?? 'DRAFT_NOT_FOUND'}</span>
      </div>
    );
  }

  const isPending = draft.status === 'Pending' || draft.status === 'Translating';
  const isFailed = draft.status === 'Failed';
  const isTranslated = draft.status === 'Translated';
  const isFinal = draft.status === 'Confirmed' || draft.status === 'Discarded';

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/superuser/catalog')}>
          <ArrowLeft className="size-4" />
          {t('superuser.catalog.backToList', { defaultValue: 'Volver al catálogo' })}
        </Button>
        <h1 className="mt-2 text-2xl font-heading text-foreground">
          {t('superuser.catalog.editTitle', {
            kind: t(`superuser.catalog.tabs.${draft.kind}`, { defaultValue: draft.kind }),
            operation: draft.operation,
            defaultValue: 'Revisión: {{kind}} · {{operation}}',
          })}
        </h1>
      </div>

      {isPending && (
        <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-3 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t('superuser.catalog.diff.translating', { defaultValue: 'Traduciendo...' })}
        </div>
      )}

      {isFailed && (
        <div className="space-y-2">
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">
                {t('superuser.catalog.diff.failed', { defaultValue: 'Traducción falló' })}
              </p>
              {draft.errorMessage && <p className="mt-0.5 font-mono text-xs">{draft.errorMessage}</p>}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleRetry}>
              {t('superuser.catalog.diff.retryAll', { defaultValue: 'Re-traducir todo' })}
            </Button>
            <Button variant="outline" onClick={handleDiscard}>
              {t('superuser.catalog.diff.discard', { defaultValue: 'Descartar' })}
            </Button>
          </div>
        </div>
      )}

      {isTranslated && (
        <>
          <CatalogDiffViewer draft={draft} onUpdated={setDraft} />
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
            <Button variant="outline" onClick={handleRetry}>
              {t('superuser.catalog.diff.retryAll', { defaultValue: 'Re-traducir todo' })}
            </Button>
            <Button variant="outline" onClick={handleDiscard}>
              {t('superuser.catalog.diff.discard', { defaultValue: 'Descartar' })}
            </Button>
            <Button onClick={handleConfirm}>
              {t('superuser.catalog.diff.confirm', { defaultValue: 'Confirmar cambios' })}
            </Button>
          </div>
        </>
      )}

      {isFinal && (
        <div className="rounded-md border border-border bg-card px-3 py-3 text-sm text-muted-foreground">
          {draft.status === 'Confirmed'
            ? t('superuser.catalog.toast.confirmed', { defaultValue: 'Cambios aplicados.' })
            : t('superuser.catalog.toast.discarded', { defaultValue: 'Borrador descartado.' })}
        </div>
      )}
    </div>
  );
}
